<script setup>
import mapboxgl from 'mapbox-gl'
import { inject, ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import AnalyzerSearchBox from '#/analyzer/AnalyzerSearchBox'
import MapToolbar from '#/analyzer/MapToolbar'
import { useAnalyzer } from '#/analyzer/useAnalyzer'
import MaxNumberOfReportsReachedPopup from '#/analyzer/maxNumberOfReportsReachedPopup'
import AppInnerLoading from '#/general/AppInnerLoading'
import MapLinks from '#/analyzer/MapLinks'

const props = defineProps(['page'])
const {
  removeAllMapboxLayersAndSources,
  loadMarker,
  mapObj,
  showQuickView,
  mapStyle,
  leadsLoading,
  leads,
  analyzerLoaded
} = useAnalyzer()
const {
  isLoggedIn,
  isBrowser,
  mapLoading,
  application,
  screen,
  global,
  partner
} = inject('Shared')

const loadMapBoxFeatureOnNextIdle = ref(false)

watch(mapStyle, (newVal) => {
  switch (newVal) {
    case 'LIGHT': mapObj.value.setStyle('mapbox://styles/mapbox/light-v10'); break
    case 'SATELLITE': mapObj.value.setStyle('mapbox://styles/mapbox/satellite-v9'); break
  }
  loadMapBoxFeatureOnNextIdle.value = true
})

onBeforeUnmount(() => {
  analyzerLoaded.value = false
  removeAllMapboxLayersAndSources()
})

onMounted(() => {
  mapboxgl.accessToken = 'pk.eyJ1IjoiZXh3b3JtIiwiYSI6ImNrcGZjYmQxYTA0NWsydW1zZTlzdXM0dWkifQ.KsByqOYmJaRdRvv-5XeBAg'
  let container
  if (application === 'WIDGET' && process.env.NODE_ENV === 'production') {
    const shadow = document.querySelector('#app').shadowRoot
    container = shadow.querySelector('#map')
  } else {
    container = 'map'
  }
  mapObj.value = new mapboxgl.Map({
    container,
    style: 'mapbox://styles/mapbox/light-v10',
    center: [8.5288, 47.3736],
    zoom: 12,
    zoomControl: false,
    preserveDrawingBuffer: true
  })
  mapObj.value.on('idle', v => {
    if (loadMapBoxFeatureOnNextIdle.value) {
      loadMarker(Boolean(application === 'WEB_APP'), screen, global.value.mapbox)
      loadMapBoxFeatureOnNextIdle.value = false
    }
  })
  mapObj.value.on('load', () => {
    loadMarker(Boolean(application === 'WEB_APP'), screen, global.value.mapbox)
  })
})

const height = computed(() => {
  return `calc(100vh - ${(screen.lt.sm && application === 'WIDGET') ? partner.value.headerHeight : '0px'})`
})

let to
if (application === 'WIDGET' && process.env.NODE_ENV === 'production') {
  const shadow = document.querySelector('#app').shadowRoot
  to = shadow.querySelector('#appContainer')
} else {
  to = '.appContainer'
}
analyzerLoaded.value = true

</script>

<template lang="pug">
.analyzerContainer
  #map.mapBoxMap(:class="page === 'WidgetAnalyzer' ? 'widgetSize' : 'webAppSize'" v-if="isBrowser")
    slot
    map-toolbar(v-if="!showQuickView || screen.gt.xs" :page="page")
      template(#filters)
        slot(name="filters")
    .analyzerInputContainer
      analyzer-search-box(:page="page" @PotentialFound="loadMarker(Boolean(application === 'WEB_APP'), screen, global.mapbox)")
        slot(name="searchBoxSlot")
    app-inner-loading(:showing='leadsLoading || mapLoading')
    max-number-of-reports-reached-popup
  map-links
</template>

<style lang="sass">
body
  overflow-x: hidden
.analyzerContainer
  position: relative
  width: 100%
  display: flex
  .webAppSize
    @media screen and (min-width: 600px)
      height: calc(100vh - 90px)
    @media screen and (max-width: 599px)
      height: calc(100vh - 80px)
  .widgetSize
    max-height: 100vh
    @media screen and (min-width: 600px)
      min-height: 700px !important
    @media screen and (max-width: 599px)
      min-height: v-bind(height) !important
  .mapBoxMap
    width: 100%
    position: relative
    max-height: 100vh
    .analyzerSearchForm
      @media screen and (min-width: 1024px)
        width: 634px !important
      @media screen and (max-width: 1023px)
      .submitBtn
        @media screen and (min-width: 1024px)
          margin-right: -40px !important
        @media screen and (max-width: 600px)
          padding: 0 !important
          position: relative !important
          top: 0 !important
          right: 0 !important
    .analyzerInputContainer
      z-index: 1
      position: absolute
      @media screen and (min-width: 1024px)
        top: 25px
        left: 100px
      @media screen and (max-width: 1023px)
        top: 20px
        display: flex
        justify-content: center
        width: 90% !important
        left: 5%
.mapboxgl-popup-content
  width: 510px !important
.gm-style-iw .gm-style-iw-c
  background: linear-gradient(153.87deg, rgba(255, 255, 255, 0.6) -4.54%, rgba(255, 255, 255, 0.1) 104.57%)
</style>
